import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, FileText, DownloadSimple, Paperclip } from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { generateReceipt } from "@/lib/receipt";
import Footer from "@/components/Footer";

const STATUS_STYLES = {
  submitted: "text-primary border-primary/40 bg-primary/10",
  under_review: "text-blue-300 border-blue-400/40 bg-blue-400/10",
  approved: "text-green-300 border-green-400/40 bg-green-400/10",
  ready: "text-green-300 border-green-400/40 bg-green-400/10",
  rejected: "text-accent border-accent/40 bg-accent/10",
};

export function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || "text-muted-foreground border-border bg-secondary";
  return (
    <span className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1 border rounded-sm ${cls}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/applications").then(({ data }) => setApps(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <main className="min-h-screen pt-24">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-primary">Dashboard</span>
            <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl mt-3">Hi, {user?.name?.split(" ")[0]}</h1>
            <p className="text-muted-foreground mt-2 text-sm">Track your license applications in real time.</p>
          </div>
          <Link to="/apply" data-testid="dashboard-new-app" className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-sm font-mono text-xs uppercase tracking-widest hover:bg-primary/90 transition-colors self-start">
            <Plus size={16} weight="bold" /> New application
          </Link>
        </div>

        {loading ? (
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground animate-pulse">Loading…</p>
        ) : apps.length === 0 ? (
          <div className="border border-dashed border-border rounded-sm py-24 text-center">
            <FileText size={40} className="text-muted-foreground mx-auto" />
            <p className="text-muted-foreground mt-4">No applications yet.</p>
            <Link to="/apply" className="text-primary font-mono text-sm uppercase tracking-widest hover:underline mt-2 inline-block">Start your first one →</Link>
          </div>
        ) : (
          <div className="space-y-5" data-testid="dashboard-app-list">
            {apps.map((a, i) => (
              <motion.div
                key={a.app_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="border border-border rounded-sm p-6 bg-card" data-testid={`dashboard-app-${a.app_id}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-primary">{a.app_id}</p>
                    <h3 className="font-display font-bold uppercase tracking-tight text-xl mt-1">{a.service_type}</h3>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
                {/* Documents + receipt */}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {(a.documents || []).map((d) => (
                    <a
                      key={d.file_id}
                      href={`${API}/files/${d.storage_path}?auth=${localStorage.getItem("rds_token")}`}
                      target="_blank" rel="noopener noreferrer"
                      data-testid={`doc-link-${d.file_id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-sm font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                    >
                      <Paperclip size={12} /> {d.name}
                    </a>
                  ))}
                  <button
                    onClick={() => generateReceipt(a)}
                    data-testid={`receipt-btn-${a.app_id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/40 rounded-sm font-mono text-[10px] uppercase tracking-widest text-primary hover:bg-primary/20 transition-colors ml-auto"
                  >
                    <DownloadSimple size={12} /> Receipt
                  </button>
                </div>
                {/* Timeline */}
                <div className="mt-6 border-t border-border pt-5 space-y-4">
                  {a.timeline.map((t, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-2.5 h-2.5 rounded-full ${idx === a.timeline.length - 1 ? "bg-primary" : "bg-muted-foreground"}`} />
                        {idx < a.timeline.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                      </div>
                      <div className="pb-1">
                        <p className="text-sm capitalize">{t.status.replace(/_/g, " ")} <span className="text-muted-foreground">— {t.remark}</span></p>
                        <p className="font-mono text-[10px] text-muted-foreground mt-0.5">{new Date(t.at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
